import { Box, Spinner, Text } from "@chakra-ui/react";
import { useAuth } from "@verivest/shared-components";
import {
  apiGroupedInvestmentModel,
  useGroupedInvestmentModel,
} from "@verivest/shared-components/src/api/hooks/models/useGroupedInvestmentModel/";
import {
  apiInvestmentModel,
  useInvestmentModel,
} from "@verivest/shared-components/src/api/hooks/models/useInvestmentModel";
import apiInvestmentsSummaryModel from "@verivest/shared-components/src/api/hooks/models/useInvestmentsSummaryModel/apiInvestmentsSummaryModel";
import useInvestmentsSummaryModel from "@verivest/shared-components/src/api/hooks/models/useInvestmentsSummaryModel/useInvestmentsSummaryModel";
import { VeriAsyncRouteComponentType } from "@verivest/shared-components/src/defines/after-js-types";
import DynamicConfigFlags from "@verivest/shared-components/src/dynamic-config/configs";
import useGetDataOrDefaults from "@verivest/shared-components/src/hooks/data/useGetDataOrDefaults";
import { useToastError } from "@verivest/shared-components/src/hooks/ui/toast/useToastError";
import React, { useEffect } from "react";
import { SectionContainer } from "../../../components";
import { WLManagerInvestmentsSummary } from "../../../components/investor-portal/white-label/manager/investments/WLManagerInvestmentsSummary";
import { WLManagerNoInvestments } from "../../../components/investor-portal/white-label/manager/investments/WLManagerNoInvestments";
import {
  sortOption,
  WLManagerNotPendingInvestments,
} from "../../../components/investor-portal/white-label/manager/investments/WLManagerNotPendingInvestments";
import { SortOptionType } from "../../../components/investor-portal/white-label/manager/investments/WLManagerNotPendingInvestments.types";
import { WLManagerPendingInvestments } from "../../../components/investor-portal/white-label/manager/investments/WLManagerPendingInvestments";
import { ManagerWhiteLabelRouteParams } from "../../../routes/investorPortalPages.types";
import DynamicConfig from "../../../utils/DynamicConfig";
import { removeTSRequiredPropsWarn } from "../../../utils/removeTSRequiredPropsWarn";
import {
  ManagerWhiteLabelInvestmentsServerInitialProps,
  WhiteLabelPageExtraProps,
} from "./types";

export const InvestmentsPage: VeriAsyncRouteComponentType<
  ManagerWhiteLabelInvestmentsServerInitialProps & WhiteLabelPageExtraProps,
  ManagerWhiteLabelRouteParams
> = ({ manager, ...rest }) => {
  const managerId = manager?.id;

  const investmentModel = useInvestmentModel();
  const summaryModel = useInvestmentsSummaryModel();
  const groupedInvestmentModel = useGroupedInvestmentModel();
  const toastError = useToastError();

  const { auth } = useAuth();
  const profile = auth.getProfile();

  const [filter, setFilter] = React.useState<SortOptionType>(sortOption.Active);
  /*useEffect(() => {
    refreshGroupedInvestments();
  }, [filter]);*/

  const { data: summary, refresh: refreshSummary } = useGetDataOrDefaults(
    rest.summary,
    async () =>
      (await summaryModel.getSummaryByManagerId(String(managerId)))?.data,
  );

  const { data: groupedInvestments, refresh: refreshGroupedInvestments } =
    useGetDataOrDefaults(
      rest.serverGroupedInvestments,
      async () =>
        (
          await groupedInvestmentModel.getCompleted(
            filter.sortBy,
            managerId as string,
            profile?.id,
          )
        )?.data || [],
    );

  const { data: pendingInvestments, refresh: refreshPendingInvestments } =
    useGetDataOrDefaults(
      rest.serverPendingInvestments,
      async () =>
        (await investmentModel.getPending(managerId as string, profile?.id))
          ?.data || [],
    );

  // while trying to use wrapApiRequest
  useEffect(() => {
    summaryModel.error && toastError(summaryModel.error);
    investmentModel.error && toastError(investmentModel.error);
    groupedInvestmentModel.error && toastError(groupedInvestmentModel.error);
  }, [summaryModel.error, investmentModel.error, groupedInvestmentModel.error]);

  const refreshAll = () => {
    refreshPendingInvestments();
    refreshSummary();
    refreshGroupedInvestments();
  };

  const noInvestments =
    pendingInvestments?.length === 0 && groupedInvestments?.length === 0;

  const allIsLoading = investmentModel.isLoading && summaryModel.isLoading;

  if (!allIsLoading && noInvestments) {
    return <WLManagerNoInvestments />;
  }

  return (
    <>
      <SectionContainer
        noBorder
        className={"py-0 sm:py-6 bg-white"}
        noPadding
        {...removeTSRequiredPropsWarn}
      >
        <Text textStyle="h1" className={"mb-0 sm:mb-16"}>
          Investments
        </Text>
        {allIsLoading && <Spinner />}
        {!!pendingInvestments?.length && pendingInvestments?.length > 0 && (
          <WLManagerPendingInvestments
            manager={manager}
            investments={pendingInvestments}
            refresh={refreshAll}
          />
        )}
        {/* TODO: Hidden temporarily until https://app.shortcut.com/verivest/epic/26537 is delivered */}
        {false && !noInvestments && (
          <Box mb={16}>
            <Text textStyle="h2" mb={5}>
              Summary of Investments
            </Text>
            <WLManagerInvestmentsSummary
              isLoading={summary === undefined}
              summary={summary}
            />
          </Box>
        )}

        <WLManagerNotPendingInvestments
          filter={filter}
          investments={groupedInvestments}
          manager={manager}
          updateFilter={setFilter}
        />
      </SectionContainer>
    </>
  );
};

InvestmentsPage.getInitialProps = async (props) => {
  const result: ManagerWhiteLabelInvestmentsServerInitialProps = {};
  const { auth } = props;
  const profile = auth.getProfile();
  const dynamicConfig = new DynamicConfig();

  const serverManager = dynamicConfig.get(DynamicConfigFlags.MANAGER);
  const managerId = serverManager?.id;

  if (managerId) {
    result.manager = serverManager;

    const investmentModel = apiInvestmentModel(props);
    const groupedInvestmentModel = apiGroupedInvestmentModel(props);
    const summaryModel = apiInvestmentsSummaryModel(props);

    result.serverPendingInvestments = (
      await investmentModel.getPending(String(managerId), profile?.id)
    )?.data;

    result.summary = (
      await summaryModel.getSummaryByManagerId(String(managerId))
    )?.data;

    result.serverGroupedInvestments = (
      await groupedInvestmentModel.getCompleted(
        sortOption.Realized.sortBy,
        String(managerId),
        profile?.id,
      )
    )?.data;
  }

  return result;
};

InvestmentsPage.getChunkName = () =>
  "investor-portal-3-manager-white-label-investments-page";
